import React, { useEffect, useState } from 'react';
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputBase,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  Theme,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { Close, Check } from '@material-ui/icons';
import { green, grey, red } from '@material-ui/core/colors';

import { database, auth, fieldValue } from '../services/firebase';
import {
  questions,
  preTestCollection,
  postTestCollection,
} from '../config/preposttest';
import { deepCopy, shuffle } from '../services/utils';
import OutlinedButton from '../components/OutlinedButton';

interface IProps {
  open: boolean;
  onClose: () => void;
  type: string;
  onTestEnd: () => void;
  preScore?: number;
}
enum State {
  START = 0,
  QUESTION = 1,
  ANSWER = 2,
  RESULT = 3,
}
interface ICurrentProps {
  state: number;
  answer: string;
  index: number;
  question?: IQuestion;
}
const initialCurrentState: ICurrentProps = {
  state: State.START,
  answer: '',
  index: -1,
};

interface IScoreBoxProps {
  score: number;
  maxScore: number;
}
interface IBoxProps {
  onClick: () => void;
  type: string;
  score?: number;
  preScore?: number;
}
const useStyles = makeStyles((theme: Theme) => ({
  dialogBackground: {
    borderRadius: 10,
    backgroundColor: '#454057',
    width: '100%',
  },
  dialogTitle: {
    backgroundColor: '#564F70',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
  },
  normalQuestion: {
    color: grey[300],
    '&.Mui-focused': {
      color: grey[300],
    },
    '&.Mui-disabled': {
      color: grey[300],
    },
  },
  correctAnswer: {
    color: green[500],
  },
  incorrectAnswer: { color: red[500] },
  normalChoice: {
    color: grey[300],
  },
  numberText: {
    marginRight: '2px',
    fontSize: theme.typography.fontSize * 1.5,
    fontWeight: 'bold',
  },
  questionText: {
    lineHeight: '1.4rem',
  },
}));

const PrePostTestDialog: React.FC<IProps> = ({
  open,
  onClose,
  type,
  onTestEnd,
  preScore,
}) => {
  const [targetQuestions, setTargetQuestions] = useState<IQuestion[]>([]);
  const [currentState, setCurrentState] = useState<ICurrentProps>({
    ...initialCurrentState,
  });
  const [answers, setAnswers] = useState<IAnswer[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      let shuffleQuestions = shuffle(questions);
      const question = getShuffleChoices(shuffleQuestions[0]);
      setCurrentState({
        ...initialCurrentState,
        // question,
      });
      setTargetQuestions(shuffleQuestions);
      setAnswers([]);
    }
  }, [open]);

  const {
    dialogBackground,
    dialogTitle,
    loading,
    correctAnswer,
    incorrectAnswer,
    normalChoice,
    normalQuestion,
    numberText,
    questionText,
  } = useStyles();

  const getShuffleChoices = (question: IQuestion): IQuestion => {
    let newQuestion = deepCopy(question);
    newQuestion.answerText = newQuestion.choices[newQuestion.answer];
    newQuestion.choices = shuffle(newQuestion.choices);
    return newQuestion;
  };

  const setNextQuestion = () => {
    if (currentState.index + 1 < targetQuestions.length) {
      const nextQuestion = getShuffleChoices(
        targetQuestions[currentState.index + 1]
      );
      setCurrentState((state) => ({
        ...state,
        state: State.QUESTION,
        answer: '',
        index: state.index + 1,
        question: nextQuestion,
      }));
    }
  };
  const addAnswer = (): IAnswer[] => {
    const question = currentState.question!;
    const answer: IAnswer = {
      question: question.question,
      answer: currentState.answer,
      correctAnswer: question.answerText!,
      correct: currentState.answer === question.answerText,
    };
    setAnswers((answers) => [...answers, answer]);
    return [...answers, answer];
  };
  const next = () => {
    if (type === 'post' && currentState.state === State.QUESTION) {
      setCurrentState((state) => ({ ...state, state: State.ANSWER }));
    } else {
      addAnswer();
      setNextQuestion();
    }
  };
  const submit = async () => {
    setSubmitting(true);
    const answers = addAnswer();
    const score = answers
      .map((answer) => (answer.correct ? 1 : 0) as number)
      .reduce((sum: number, current: number) => sum + current);
    await database
      .collection(type === 'pre' ? preTestCollection : postTestCollection)
      .doc(auth.currentUser!.uid)
      .set({ answers, createdAt: fieldValue.serverTimestamp(), score });
    setSubmitting(false);
    setCurrentState((state) => ({ ...state, state: State.RESULT }));
    onTestEnd();
  };
  const isCorrectAnswer = (choice: string) =>
    (currentState.answer === choice &&
      currentState.answer === currentState.question!.answerText) ||
    (currentState.question!.answerText === choice &&
      choice === currentState.question!.answerText);
  const getAnswerTextStyle = (choice: string) =>
    currentState.state === State.ANSWER &&
    (currentState.answer === choice ||
      currentState.question!.answerText === choice)
      ? isCorrectAnswer(choice)
        ? correctAnswer
        : incorrectAnswer
      : normalChoice;
  const iconRender = (choice: string): any => {
    if (currentState.answer === choice) {
      return currentState.answer === currentState.question!.answerText ? (
        <Check className={correctAnswer} />
      ) : (
        <Close className={incorrectAnswer} />
      );
    } else if (currentState.question!.answerText === choice) {
      return choice === currentState.question!.answerText ? (
        <Check className={correctAnswer} />
      ) : (
        <Close className={incorrectAnswer} />
      );
    }
    return null;
  };
  const questionRender = () => {
    return (
      <Box padding={1}>
        <Box>
          <FormControl disabled={currentState.state === State.ANSWER}>
            <FormLabel component="legend" className={normalQuestion}>
              <Box display="flex" alignItems="baseline">
                <div className={numberText}>{currentState.index + 1}</div>
                <div
                  style={{ marginRight: '10px' }}
                >{` /${targetQuestions.length}`}</div>
                <div className={questionText}>{` ${
                  currentState.question!.question
                }`}</div>
              </Box>
            </FormLabel>

            <Box height={8} />
            <RadioGroup
              value={`${currentState.answer}`}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const { value } = e.target;
                setCurrentState((state) => ({
                  ...state,
                  answer: value,
                }));
              }}
            >
              {currentState.question!.choices.map(
                (choice: string, index: number) => (
                  <Box display="flex" alignItems="center" key={index}>
                    <FormControlLabel
                      value={choice}
                      control={<Radio className={getAnswerTextStyle(choice)} />}
                      label={
                        <Typography className={getAnswerTextStyle(choice)}>
                          {choice}
                        </Typography>
                      }
                    />
                    {currentState.state === State.ANSWER && iconRender(choice)}
                  </Box>
                )
              )}
            </RadioGroup>
          </FormControl>
        </Box>
        <Box height={8} />
        <Box display="flex" justifyContent="center">
          {currentState.index < targetQuestions.length - 1 ? (
            <OutlinedButton
              disabled={currentState.answer === ''}
              onClick={next}
            >
              ถัดไป
            </OutlinedButton>
          ) : (
            <OutlinedButton
              disabled={currentState.answer === ''}
              onClick={submit}
            >
              Submit
            </OutlinedButton>
          )}
        </Box>
      </Box>
    );
  };
  const stateRender = () => {
    switch (currentState.state) {
      case State.START:
        return <StartBox type={type} onClick={setNextQuestion} />;
      case State.QUESTION:
      case State.ANSWER:
        return questionRender();
      case State.RESULT:
        if (type === 'pre') {
          return (
            <ResultPreBox
              type={type}
              onClick={onClose}
              score={answers
                .map((answer) => (answer.correct ? 1 : 0) as number)
                .reduce((sum: number, current: number) => sum + current)}
            />
          );
        } else {
          return (
            <ResultPostBox
              type={type}
              onClick={onClose}
              score={answers
                .map((answer) => (answer.correct ? 1 : 0) as number)
                .reduce((sum: number, current: number) => sum + current)}
              preScore={preScore}
            />
          );
        }
    }
  };
  return (
    <>
      <Dialog
        classes={{ paper: dialogBackground }}
        open={open}
        onClose={onClose}
        disableBackdropClick
      >
        <DialogTitle className={dialogTitle}>
          <Box display="flex" justifyContent="space-between">
            {`${type === 'pre' ? 'Pre Test' : 'Post Test'}`}
            <IconButton
              size="small"
              color="secondary"
              onClick={onClose}
              disabled={submitting}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            display="flex"
            flexDirection="column"
            width="100%"
            pt={1}
            padding="10px"
          >
            {stateRender()}
          </Box>

          <Backdrop open={submitting} className={loading}>
            <CircularProgress color="secondary" />
          </Backdrop>
        </DialogContent>
      </Dialog>
    </>
  );
};

const StartBox: React.FC<IBoxProps> = ({ type, onClick }) => {
  return (
    <>
      <Box display="flex" justifyContent="center">
        <div
          style={{
            width: '60%',
            height: 120,
            backgroundImage: 'url(/images/logo-small.png)',
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
          }}
        />
      </Box>
      <Box display="flex" flexDirection="column">
        <Typography variant="h3">Cyber Security คืออะไร ?</Typography>
        <Box height={8} />
        <Typography variant="h4">
          เพิ่มความตระหนักในการใช้อินเทอร์เน็ต
        </Typography>
        <Box height={8} />
        <Typography variant="body1">
          {type === 'pre'
            ? ` ก่อนที่จะเพลิดเพลินไปกับเกม Learn Cyber Sec สละเวลาวัดความรู้ความเข้าใจเกี่ยวกับความปลอดภัยบนโลกออนไลน์กันสักนิด`
            : `หลังจากเล่นเกมจบแล้ว มาวัดความรู้ความเข้าใจกันสักหน่อยดีกว่า ว่าคุณเก่งมากขึ้นแค่ไหน`}
        </Typography>
        <Box height={16} />
        <Button variant="contained" color="primary" onClick={onClick}>
          เริ่มทำแบบทดสอบ
        </Button>
      </Box>
    </>
  );
};

const ScoreBox: React.FC<IScoreBoxProps> = ({ score, maxScore }) => {
  const { numberText } = useStyles();
  return (
    <Box
      display="flex"
      border={1}
      m={1}
      style={{ width: '10rem', height: '10rem' }}
      borderColor={grey[500]}
      justifyContent="center"
      alignItems="center"
    >
      <Box display="flex" alignItems="baseline">
        <Typography variant="h2">{score}</Typography>
        <Typography variant="h4">{`/ ${maxScore}`}</Typography>
      </Box>
    </Box>
  );
};

const ResultPreBox: React.FC<IBoxProps> = ({ onClick, score }) => {
  return (
    <>
      <Box display="flex" flexDirection="column" alignItems="center">
        <Typography variant="h5">ทำแบบทดสอบจบแล้ว!! คุณได้คะแนน</Typography>
        <Box height={8} />
        <ScoreBox score={score as number} maxScore={questions.length} />
        <Box height={8} />
        <Typography variant="h6">
          มาสนุกกับเกมกันเลย!! แล้วอย่าลืมเก็บเกี่ยวความรู้ให้เต็มที่นะ
        </Typography>
        <Box height={16} />
        <OutlinedButton onClick={onClick}>ตกลง</OutlinedButton>
      </Box>
    </>
  );
};

const ResultPostBox: React.FC<IBoxProps> = ({ onClick, score, preScore }) => {
  return (
    <>
      <Box display="flex" flexDirection="column" alignItems="center">
        <Typography variant="h5">ทำแบบทดสอบจบแล้ว!! คุณได้คะแนน</Typography>
        <Box height={8} />
        <Box display="flex">
          <ScoreBox score={preScore as number} maxScore={questions.length} />
          <ScoreBox score={score as number} maxScore={questions.length} />
        </Box>
        <Box height={8} />
        <Typography variant="h6">
          คุณสามารถเล่นเกมซ้ำได้ และดูข่าวสารต่างๆภายในเว็บไซต์ได้ตลอดเวลา
          แล้วอย่าลืมนำความรู้ที่ได้ไปใช้ในชีวิตประจำวันด้วยนะ
        </Typography>
        <Box height={16} />
        <OutlinedButton onClick={onClick}>ตกลง</OutlinedButton>
      </Box>
    </>
  );
};

export default PrePostTestDialog;
